import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowPathIcon,
  BackwardIcon,
  CheckIcon,
  ForwardIcon,
  PauseIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlayIcon,
  SpeakerWaveIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  getActiveClipEffectItems,
  getContainedMediaRect,
  getCurrentSegment,
  mapSubtitleSegmentsToTextRanges,
  resolveSubtitleAppearance,
  resolveSubtitleInlineFontScale,
  resolveSubtitleInlineStyle,
  sliceSubtitleTextRuns,
  splitSubtitleSegments,
  resolveSubtitleLayout,
  type SubtitleSegmentRange,
  type SubtitleTextRun,
} from '@packages/global';

import ClipEffectsRenderer from '../../clip-effects/ClipEffectsRenderer';
import { useClipEffectsPlayer } from '../../clip-effects/useClipEffectsPlayer';
import { useVideoEditor } from '../../context/VideoEditorContext';
import { videoClipApi } from '../../../../services/user/videoclip.api';
import GlobalLoading from '../../../GlobalLoading';
import type { ClipContextNodeData } from '../types';

interface ClipFinalPreviewProps {
  clip: ClipContextNodeData['videoClip'];
}

type PreviewImageTarget = 'start' | 'end' | 'storyboard';

type FinalPreviewMedia = {
  type: 'video' | 'image';
  url: string;
  label: string;
};

function getFinalPreviewMedia(clip: ClipContextNodeData['videoClip']): FinalPreviewMedia | null {
  if (clip.videoUrl) {
    return { type: 'video', url: clip.videoUrl, label: '视频' };
  }

  if (clip.startImage) {
    return { type: 'image', url: clip.startImage, label: '开始帧' };
  }

  if (clip.storyboardImage) {
    return { type: 'image', url: clip.storyboardImage, label: '分镜图' };
  }

  if (clip.endImage) {
    return { type: 'image', url: clip.endImage, label: '结束帧' };
  }

  return null;
}

function getAspectRatioCss(aspectRatio?: string | null): string {
  if (!aspectRatio) return '16 / 9';
  const match = aspectRatio.match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
  if (!match) return '16 / 9';
  return `${match[1]} / ${match[2]}`;
}

function ClipFinalPreviewModal({
  clip,
  onClose,
}: {
  clip: ClipContextNodeData['videoClip'];
  onClose: () => void;
}) {
  const { state, actions } = useVideoEditor();
  const { editConfig, episode, subtitleEnabled } = state;
  const [mainImage, setMainImage] = useState<PreviewImageTarget>('start');
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [mediaDuration, setMediaDuration] = useState(clip.duration ?? 0);
  const [isTrimEditing, setIsTrimEditing] = useState(false);
  const [tempTrimStart, setTempTrimStart] = useState(0);
  const [tempTrimEnd, setTempTrimEnd] = useState(0);
  const [isSavingTrim, setIsSavingTrim] = useState(false);
  const [videoAspectRatio, setVideoAspectRatio] = useState(16 / 9);
  const [imageAspectRatio, setImageAspectRatio] = useState(16 / 9);
  const [subtitleFontFamily, setSubtitleFontFamily] = useState('');
  const [previewContainerSize, setPreviewContainerSize] = useState({ width: 0, height: 0 });
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const dialogueAudioRef = useRef<HTMLAudioElement | null>(null);
  const soundEffectAudioRef = useRef<HTMLAudioElement | null>(null);
  const bgmAudioRef = useRef<HTMLAudioElement | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const imagePlaybackFrameRef = useRef<number | null>(null);
  const imagePlaybackStartedAtRef = useRef(0);
  const imagePlaybackStartTimeRef = useRef(0);

  const clipInfo = editConfig?.clips?.find((item) => item.clipId === clip.id);
  const configuredTrimStart = clipInfo?.trimStart ?? 0;
  const configuredTrimEnd = clipInfo?.trimEnd;
  const sourceDuration = Math.max(
    mediaDuration,
    clip.duration ?? 0,
    configuredTrimEnd ?? 0,
    configuredTrimStart + (clipInfo?.duration ?? 0),
  );
  const resolvedTrimEnd = configuredTrimEnd ?? sourceDuration;
  const trimStart = isTrimEditing ? tempTrimStart : configuredTrimStart;
  const trimEnd = isTrimEditing ? tempTrimEnd : resolvedTrimEnd;
  const clipDuration = Math.max(0, trimEnd - trimStart);
  const subtitleTrackStyle = editConfig?.tracks?.subtitle?.style;
  const trackEnabled = {
    video: editConfig?.tracks?.video?.enabled ?? true,
    audio: editConfig?.tracks?.audio?.enabled ?? true,
    subtitle: editConfig?.tracks?.subtitle?.enabled ?? true,
    bgm: editConfig?.tracks?.bgm?.enabled ?? true,
  };
  const clipVideoVolume = clipInfo?.videoVolume ?? 0;
  const trackVideoVolume = editConfig?.tracks?.video?.volume ?? 1;
  const videoVolume = trackEnabled.video ? Math.min(1, Math.max(0, clipVideoVolume * trackVideoVolume)) : 0;
  const clipDialogueVolume = clipInfo?.dialogueVolume ?? 1;
  const trackAudioVolume = editConfig?.tracks?.audio?.volume ?? 0.8;
  const dialogueVolume = trackEnabled.audio ? Math.min(1, Math.max(0, clipDialogueVolume * trackAudioVolume)) : 0;
  const bgmVolume = trackEnabled.bgm ? Math.min(1, Math.max(0, (editConfig?.tracks?.bgm?.volume ?? 0.3) * 0.6)) : 0;
  const bgmUrl = episode?.bgmUrl;
  const modalAspectStyle = useMemo<CSSProperties & Record<'--clip-canvas-media-aspect-ratio', string>>(
    () => ({ '--clip-canvas-media-aspect-ratio': getAspectRatioCss(episode?.videoAspectRatio) }),
    [episode?.videoAspectRatio],
  );
  const hasVideo = !!clip.videoUrl;
  const imageMap: Record<PreviewImageTarget, string | undefined> = {
    start: clip.startImage,
    end: clip.endImage,
    storyboard: clip.storyboardImage,
  };
  const labelMap: Record<PreviewImageTarget, string> = {
    start: '开始帧',
    end: '结束帧',
    storyboard: '分镜图',
  };
  const fallbackImageTarget = (['start', 'storyboard', 'end'] as PreviewImageTarget[])
    .find((key) => imageMap[key]) ?? 'start';
  const activeImageTarget = imageMap[mainImage] ? mainImage : fallbackImageTarget;
  const primaryImage = imageMap[activeImageTarget];
  const thumbnails = (['start', 'end', 'storyboard'] as PreviewImageTarget[])
    .filter((key) => key !== activeImageTarget && imageMap[key]);
  const activeAspectRatio = hasVideo ? videoAspectRatio : imageAspectRatio;
  const localTime = currentTime;
  const audioUrl = clip.scriptAudioUrl || clip.soundEffectAudioUrl;

  useEffect(() => {
    setMainImage('start');
    setCurrentTime(0);
    setIsPlaying(false);
    setIsTrimEditing(false);
    setMediaDuration(clip.duration ?? 0);
  }, [clip.id]);

  useEffect(() => {
    if (isTrimEditing) return;
    setTempTrimStart(configuredTrimStart);
    setTempTrimEnd(resolvedTrimEnd);
  }, [configuredTrimStart, isTrimEditing, resolvedTrimEnd]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = videoVolume;
    video.muted = videoVolume <= 0;
  }, [videoVolume]);

  useEffect(() => () => {
    if (imagePlaybackFrameRef.current !== null) {
      cancelAnimationFrame(imagePlaybackFrameRef.current);
    }
    dialogueAudioRef.current?.pause();
    soundEffectAudioRef.current?.pause();
    bgmAudioRef.current?.pause();
  }, []);

  useEffect(() => {
    const element = previewContainerRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setPreviewContainerSize({ width: rect.width, height: rect.height });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fontUrl = subtitleTrackStyle?.fontUrl;
    const fontKey = subtitleTrackStyle?.fontId || subtitleTrackStyle?.fontName || fontUrl;

    if (!fontUrl || !fontKey) {
      setSubtitleFontFamily('');
      return;
    }

    const family = `VideoAgentSubtitleFont-${String(fontKey).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    let cancelled = false;

    const loadFont = async () => {
      try {
        const fontFace = new FontFace(family, `url(${fontUrl})`);
        await fontFace.load();
        document.fonts.add(fontFace);
        if (!cancelled) {
          setSubtitleFontFamily(family);
        }
      } catch (error) {
        console.error('Failed to load subtitle font:', error);
        if (!cancelled) {
          setSubtitleFontFamily('');
        }
      }
    };

    loadFont();

    return () => {
      cancelled = true;
    };
  }, [subtitleTrackStyle?.fontId, subtitleTrackStyle?.fontName, subtitleTrackStyle?.fontUrl]);

  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;

    const updateImageAspectRatio = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setImageAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };

    if (img.complete) {
      updateImageAspectRatio();
      return;
    }

    img.addEventListener('load', updateImageAspectRatio);
    return () => img.removeEventListener('load', updateImageAspectRatio);
  }, [primaryImage]);

  const subtitleLayout = resolveSubtitleLayout(activeAspectRatio, subtitleTrackStyle?.fontSize);
  const subtitleStyle = {
    fontSize: `${subtitleLayout.fontSizeScale}rem`,
    maxWidth: `${subtitleLayout.maxWidthPercent}%`,
    bottom: subtitleLayout.bottomOffset,
  };
  const displaySubtitleScript = clip.script || '';
  const subtitleAppearance = resolveSubtitleAppearance(
    subtitleTrackStyle,
    subtitleTrackStyle?.fontSize ?? 18,
  );
  const subtitleTextStyle = useMemo<React.CSSProperties>(() => ({
    fontSize: subtitleStyle.fontSize,
    color: subtitleAppearance.textColor,
    fontFamily: subtitleFontFamily || 'inherit',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    whiteSpace: 'pre-wrap',
    hyphens: 'none',
    textShadow: subtitleAppearance.cssTextShadow,
    display: 'inline-block',
    maxWidth: '100%',
  }), [
    subtitleAppearance.cssTextShadow,
    subtitleAppearance.textColor,
    subtitleFontFamily,
    subtitleStyle.fontSize,
  ]);
  const subtitleContainerPosition = subtitleTrackStyle?.position === 'center'
    ? { top: '50%', transform: 'translate(-50%, -50%)' }
    : { bottom: subtitleStyle.bottom, transform: 'translateX(-50%)' };

  const currentSubtitleSegment = useMemo<SubtitleSegmentRange | null>(() => {
    if (!displaySubtitleScript) return null;

    if (clipDuration <= 0) {
      return {
        text: displaySubtitleScript,
        startOffset: 0,
        endOffset: clipDuration || 0,
        textStart: 0,
        textEnd: displaySubtitleScript.length,
      };
    }

    const segments = splitSubtitleSegments(displaySubtitleScript, clipDuration, 1.0);
    if (segments.length === 0) {
      return {
        text: displaySubtitleScript,
        startOffset: 0,
        endOffset: clipDuration,
        textStart: 0,
        textEnd: displaySubtitleScript.length,
      };
    }

    const rangedSegments = mapSubtitleSegmentsToTextRanges(displaySubtitleScript, segments);
    const segment = getCurrentSegment(rangedSegments, localTime);
    if (!segment) return null;

    return rangedSegments.find(
      (item) =>
        item.startOffset === segment.startOffset &&
        item.endOffset === segment.endOffset &&
        item.text === segment.text,
    ) ?? null;
  }, [displaySubtitleScript, clipDuration, localTime]);

  const currentSubtitleRuns = useMemo(() => {
    if (!displaySubtitleScript) return [] as SubtitleTextRun[];
    if (!currentSubtitleSegment) {
      return [{ text: displaySubtitleScript }] as SubtitleTextRun[];
    }

    if (typeof currentSubtitleSegment.textStart !== 'number' || typeof currentSubtitleSegment.textEnd !== 'number') {
      return [{ text: currentSubtitleSegment.text }] as SubtitleTextRun[];
    }

    return sliceSubtitleTextRuns(
      displaySubtitleScript,
      Array.isArray(clip.subtitleStyleSpans) ? clip.subtitleStyleSpans : undefined,
      { start: currentSubtitleSegment.textStart, end: currentSubtitleSegment.textEnd },
    );
  }, [displaySubtitleScript, clip.subtitleStyleSpans, currentSubtitleSegment]);

  const activeClipEffects = useMemo(() => getActiveClipEffectItems(
    clip.effectItems,
    localTime,
    clipDuration,
  ), [clip.effectItems, clipDuration, localTime]);

  useClipEffectsPlayer({
    clip,
    localTime,
    isPlaying,
    clipDuration,
  });

  const formatTime = (time: number) => {
    const safeTime = Math.max(0, Number.isFinite(time) ? time : 0);
    const minutes = Math.floor(safeTime / 60);
    const seconds = Math.floor(safeTime % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const pauseLinkedAudio = useCallback(() => {
    dialogueAudioRef.current?.pause();
    soundEffectAudioRef.current?.pause();
    bgmAudioRef.current?.pause();
  }, []);

  const syncLinkedAudio = useCallback((nextLocalTime: number) => {
    const dialogueAudio = dialogueAudioRef.current;
    const soundEffectAudio = soundEffectAudioRef.current;
    const bgmAudio = bgmAudioRef.current;

    if (dialogueAudio && Number.isFinite(dialogueAudio.duration)) {
      dialogueAudio.currentTime = Math.min(nextLocalTime, Math.max(0, dialogueAudio.duration - 0.05));
    }

    if (soundEffectAudio && Number.isFinite(soundEffectAudio.duration)) {
      soundEffectAudio.currentTime = Math.min(nextLocalTime, Math.max(0, soundEffectAudio.duration - 0.05));
    }

    if (bgmAudio && Number.isFinite(bgmAudio.duration) && bgmAudio.duration > 0) {
      const timelineTime = (clipInfo?.startTime ?? 0) + nextLocalTime;
      bgmAudio.currentTime = timelineTime % bgmAudio.duration;
    }
  }, [clipInfo?.startTime]);

  const playLinkedAudio = useCallback(() => {
    const linkedAudios: Array<HTMLAudioElement | null> = [
      dialogueAudioRef.current,
      soundEffectAudioRef.current,
      bgmAudioRef.current,
    ];

    linkedAudios.forEach((audio) => {
      if (!audio) return;
      audio.play().catch((error) => {
        console.error('Preview audio playback failed:', error);
      });
    });
  }, []);

  const seekToLocalTime = useCallback((nextTime: number) => {
    const boundedTime = Math.max(0, Math.min(clipDuration, nextTime));
    setCurrentTime(boundedTime);

    const video = videoRef.current;
    if (video) {
      video.currentTime = trimStart + boundedTime;
    }

    syncLinkedAudio(boundedTime);
  }, [clipDuration, syncLinkedAudio, trimStart]);

  const pausePreview = useCallback(() => {
    if (imagePlaybackFrameRef.current !== null) {
      cancelAnimationFrame(imagePlaybackFrameRef.current);
      imagePlaybackFrameRef.current = null;
    }
    videoRef.current?.pause();
    pauseLinkedAudio();
    setIsPlaying(false);
  }, [pauseLinkedAudio]);

  const playPreview = useCallback(() => {
    if (clipDuration <= 0) return;

    const startTime = currentTime >= clipDuration ? 0 : currentTime;
    seekToLocalTime(startTime);
    syncLinkedAudio(startTime);
    playLinkedAudio();

    if (hasVideo && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error('Preview video playback failed:', error);
      });
      return;
    }

    imagePlaybackStartTimeRef.current = startTime;
    imagePlaybackStartedAtRef.current = performance.now();
    setIsPlaying(true);
  }, [clipDuration, currentTime, hasVideo, playLinkedAudio, seekToLocalTime, syncLinkedAudio]);

  const togglePlayback = useCallback(() => {
    if (isPlaying) {
      pausePreview();
    } else {
      playPreview();
    }
  }, [isPlaying, pausePreview, playPreview]);

  useEffect(() => {
    if (hasVideo || !isPlaying || clipDuration <= 0) return;

    const tick = () => {
      const elapsed = (performance.now() - imagePlaybackStartedAtRef.current) / 1000;
      const nextTime = imagePlaybackStartTimeRef.current + elapsed;

      if (nextTime >= clipDuration) {
        setCurrentTime(clipDuration);
        pausePreview();
        return;
      }

      setCurrentTime(nextTime);
      imagePlaybackFrameRef.current = requestAnimationFrame(tick);
    };

    imagePlaybackFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (imagePlaybackFrameRef.current !== null) {
        cancelAnimationFrame(imagePlaybackFrameRef.current);
        imagePlaybackFrameRef.current = null;
      }
    };
  }, [clipDuration, hasVideo, isPlaying, pausePreview]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.volume = videoVolume;
      video.muted = videoVolume <= 0;
    }
    if (dialogueAudioRef.current) {
      dialogueAudioRef.current.volume = dialogueVolume;
      dialogueAudioRef.current.muted = dialogueVolume <= 0;
    }
    if (soundEffectAudioRef.current) {
      soundEffectAudioRef.current.volume = dialogueVolume;
      soundEffectAudioRef.current.muted = dialogueVolume <= 0;
    }
    if (bgmAudioRef.current) {
      bgmAudioRef.current.volume = bgmVolume;
      bgmAudioRef.current.muted = bgmVolume <= 0;
    }
  }, [bgmVolume, dialogueVolume, videoVolume]);

  const startTrimEditing = () => {
    pausePreview();
    setTempTrimStart(configuredTrimStart);
    setTempTrimEnd(resolvedTrimEnd);
    setIsTrimEditing(true);
  };

  const cancelTrimEditing = () => {
    setIsTrimEditing(false);
    setTempTrimStart(configuredTrimStart);
    setTempTrimEnd(resolvedTrimEnd);
    seekToLocalTime(0);
  };

  const resetTrimEditing = () => {
    setTempTrimStart(0);
    setTempTrimEnd(sourceDuration);
    seekToLocalTime(0);
  };

  const updateTrimPosition = (type: 'start' | 'end', time: number) => {
    const boundedTime = Math.max(0, Math.min(sourceDuration, time));
    if (type === 'start' && boundedTime < tempTrimEnd - 0.1) {
      setTempTrimStart(boundedTime);
      seekToLocalTime(0);
    } else if (type === 'end' && boundedTime > tempTrimStart + 0.1) {
      setTempTrimEnd(boundedTime);
      if (currentTime > boundedTime - tempTrimStart) {
        seekToLocalTime(Math.max(0, boundedTime - tempTrimStart));
      }
    }
  };

  const handleTrimHandleDrag = (type: 'start' | 'end', event: React.MouseEvent) => {
    if (!isTrimEditing) return;
    event.preventDefault();
    event.stopPropagation();

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!timelineRef.current || sourceDuration <= 0) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const percent = (moveEvent.clientX - rect.left) / rect.width;
      updateTrimPosition(type, percent * sourceDuration);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const applyTrimChanges = async () => {
    if (!editConfig || !clipInfo || !clip.id) return;

    const newDuration = tempTrimEnd - tempTrimStart;
    const updatedClips = editConfig.clips.map((item) => (
      item.clipId === clip.id
        ? { ...item, trimStart: tempTrimStart, trimEnd: tempTrimEnd, duration: newDuration }
        : item
    ));

    const currentClipIndex = updatedClips.findIndex((item) => item.clipId === clip.id);
    for (let index = currentClipIndex + 1; index < updatedClips.length; index += 1) {
      updatedClips[index] = {
        ...updatedClips[index],
        startTime: updatedClips[index - 1].startTime + updatedClips[index - 1].duration,
      };
    }

    const totalDuration = updatedClips.reduce((sum, item) => sum + item.duration, 0);
    const nextConfig = {
      ...editConfig,
      clips: updatedClips,
      timeline: { ...editConfig.timeline, totalDuration },
    };

    setIsSavingTrim(true);
    try {
      await actions.saveEditConfigImmediately(nextConfig);
      await videoClipApi.updateVideoClip(clip.id, { duration: newDuration });
      setIsTrimEditing(false);
      seekToLocalTime(0);
    } finally {
      setIsSavingTrim(false);
    }
  };

  const renderSubtitleRuns = (runs: SubtitleTextRun[]) => (
    <>
      {runs.map((run, index) => {
        const inlineStyle = resolveSubtitleInlineStyle(run.style);
        const inferredInlineScale = resolveSubtitleInlineFontScale(inlineStyle);
        return (
          <span
            key={`${run.text}-${index}`}
            style={inlineStyle ? {
              color: inlineStyle.color || subtitleAppearance.textColor,
              fontSize: inferredInlineScale
                ? `calc(${subtitleStyle.fontSize} * ${inferredInlineScale})`
                : inlineStyle.fontSize ? `${inlineStyle.fontSize}px` : undefined,
              fontWeight: inlineStyle.bold ? 700 : undefined,
              fontStyle: inlineStyle.italic ? 'italic' : undefined,
              fontFamily: inlineStyle.fontName || undefined,
            } : undefined}
          >
            {run.text}
          </span>
        );
      })}
    </>
  );

  const renderCompositeOverlays = () => (
    <>
      {activeClipEffects.some((effect) => !!effect.sticker) && (
        <div
          className="clip-canvas-final-preview-modal__effects"
          style={getContainedMediaRect(
            previewContainerSize.width,
            previewContainerSize.height,
            activeAspectRatio,
          )}
        >
          <ClipEffectsRenderer effectItems={activeClipEffects} baseSubtitleStyle={subtitleTrackStyle} />
        </div>
      )}

      {subtitleEnabled && trackEnabled.subtitle && displaySubtitleScript && currentSubtitleRuns.length > 0 && (
        <div
          className="clip-canvas-final-preview-modal__subtitle"
          style={{
            ...subtitleContainerPosition,
            maxWidth: subtitleStyle.maxWidth,
            width: '100%',
          }}
        >
          <p style={subtitleTextStyle}>
            {renderSubtitleRuns(currentSubtitleRuns)}
          </p>
        </div>
      )}
    </>
  );

  const trimSelectionLeft = sourceDuration > 0 ? (trimStart / sourceDuration) * 100 : 0;
  const trimSelectionWidth = sourceDuration > 0 ? ((trimEnd - trimStart) / sourceDuration) * 100 : 0;
  const currentProgress = clipDuration > 0 ? (currentTime / clipDuration) * 100 : 0;

  return (
    <div
      className="clip-canvas-final-preview-modal nodrag nopan nowheel"
      style={modalAspectStyle}
      onClick={(event) => {
        event.stopPropagation();
        onClose();
      }}
    >
      <div
        className="clip-canvas-final-preview-modal__panel"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="clip-canvas-final-preview-modal__topbar">
          <div>
            <div className="clip-canvas-final-preview-modal__eyebrow">最终预览</div>
            <div className="clip-canvas-final-preview-modal__title">{clip.title || clip.sceneName || '未命名片段'}</div>
          </div>
          <button
            type="button"
            className="clip-canvas-final-preview-modal__close"
            onClick={onClose}
            aria-label="关闭预览"
            title="关闭预览"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {hasVideo && sourceDuration > 0 && (
          <div className="clip-canvas-final-preview-modal__trim">
            <div className="clip-canvas-final-preview-modal__trim-duration">
              {formatTime(clipDuration)}
            </div>
            <div ref={timelineRef} className="clip-canvas-final-preview-modal__trim-track">
              <div
                className={`clip-canvas-final-preview-modal__trim-range ${isTrimEditing ? 'is-editing' : ''}`}
                style={{
                  left: `${trimSelectionLeft}%`,
                  width: `${trimSelectionWidth}%`,
                }}
              />
              <button
                type="button"
                className={`clip-canvas-final-preview-modal__trim-handle ${isTrimEditing ? 'is-editing' : ''}`}
                style={{ left: `${trimSelectionLeft}%` }}
                onMouseDown={(event) => handleTrimHandleDrag('start', event)}
                aria-label="调整裁切开始"
              />
              <button
                type="button"
                className={`clip-canvas-final-preview-modal__trim-handle ${isTrimEditing ? 'is-editing' : ''}`}
                style={{ left: `${sourceDuration > 0 ? (trimEnd / sourceDuration) * 100 : 0}%` }}
                onMouseDown={(event) => handleTrimHandleDrag('end', event)}
                aria-label="调整裁切结束"
              />
            </div>
            {!isTrimEditing ? (
              <button
                type="button"
                className="clip-canvas-final-preview-modal__tool-button"
                onClick={startTrimEditing}
                aria-label="编辑裁切"
                title="编辑裁切"
              >
                <PencilSquareIcon className="h-5 w-5" />
              </button>
            ) : (
              <div className="clip-canvas-final-preview-modal__trim-actions">
                <button
                  type="button"
                  className="clip-canvas-final-preview-modal__tool-button"
                  onClick={resetTrimEditing}
                  aria-label="重置裁切"
                  title="重置裁切"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  className="clip-canvas-final-preview-modal__tool-button"
                  onClick={cancelTrimEditing}
                  aria-label="取消裁切"
                  title="取消裁切"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  className="clip-canvas-final-preview-modal__tool-button is-primary"
                  onClick={applyTrimChanges}
                  aria-label="确认裁切"
                  title="确认裁切"
                >
                  <CheckIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        )}

        <div ref={previewContainerRef} className="clip-canvas-final-preview-modal__stage">
          {hasVideo ? (
            <video
              ref={videoRef}
              src={clip.videoUrl}
              playsInline
              preload="metadata"
              onLoadedMetadata={(event) => {
                const video = event.currentTarget;
                if (video.videoWidth && video.videoHeight) {
                  setVideoAspectRatio(video.videoWidth / video.videoHeight);
                }
                if (video.duration > 0) {
                  setMediaDuration(video.duration);
                }
                if (trimStart > 0) {
                  video.currentTime = trimStart;
                }
                setCurrentTime(0);
              }}
              onTimeUpdate={(event) => {
                const video = event.currentTarget;
                const sourceTime = video.currentTime;
                if (trimEnd !== undefined && sourceTime >= trimEnd) {
                  video.pause();
                  setIsPlaying(false);
                  pauseLinkedAudio();
                  setCurrentTime(Math.max(0, trimEnd - trimStart));
                  return;
                }
                setCurrentTime(Math.max(0, sourceTime - trimStart));
              }}
              onPlay={(event) => {
                const video = event.currentTarget;
                if (video.currentTime < trimStart || (trimEnd !== undefined && video.currentTime >= trimEnd)) {
                  video.currentTime = trimStart;
                }
                syncLinkedAudio(Math.max(0, video.currentTime - trimStart));
                playLinkedAudio();
                setIsPlaying(true);
              }}
              onPause={() => {
                setIsPlaying(false);
                pauseLinkedAudio();
              }}
              onEnded={() => {
                setIsPlaying(false);
                pauseLinkedAudio();
              }}
            />
          ) : primaryImage ? (
            <img ref={imageRef} src={primaryImage} alt={labelMap[activeImageTarget]} />
          ) : (
            <div className="clip-canvas-final-preview-modal__empty">
              <PhotoIcon className="h-10 w-10" />
              <span>暂无画面内容</span>
            </div>
          )}

          {!hasVideo && thumbnails.length > 0 && (
            <div className="clip-canvas-final-preview-modal__thumbnails">
              {thumbnails.map((key) => (
                <button
                  key={key}
                  type="button"
                  className="clip-canvas-final-preview-modal__thumbnail"
                  onClick={() => setMainImage(key)}
                >
                  <img src={imageMap[key]} alt={labelMap[key]} />
                  <span>{labelMap[key]}</span>
                </button>
              ))}
            </div>
          )}

          {renderCompositeOverlays()}
        </div>

        <div className="clip-canvas-final-preview-modal__hidden-media">
          {clip.scriptAudioUrl && (
            <audio ref={dialogueAudioRef} src={clip.scriptAudioUrl} preload="metadata" />
          )}
          {clip.soundEffectAudioUrl && (
            <audio ref={soundEffectAudioRef} src={clip.soundEffectAudioUrl} preload="metadata" />
          )}
          {bgmUrl && (
            <audio ref={bgmAudioRef} src={bgmUrl} preload="metadata" loop />
          )}
        </div>

        <div className="clip-canvas-final-preview-modal__transport">
          <div className="clip-canvas-final-preview-modal__progress">
            <div
              className="clip-canvas-final-preview-modal__progress-fill"
              style={{ width: `${currentProgress}%` }}
            />
            <input
              type="range"
              min={0}
              max={Math.max(clipDuration, 0.1)}
              step={0.05}
              value={Math.min(currentTime, clipDuration)}
              onChange={(event) => seekToLocalTime(Number(event.target.value))}
              aria-label="预览进度"
            />
          </div>
          <div className="clip-canvas-final-preview-modal__transport-row">
            <div className="clip-canvas-final-preview-modal__mix">
              <span>视频 {Math.round(videoVolume * 100)}%</span>
              <span>对白 {Math.round(dialogueVolume * 100)}%</span>
              {bgmUrl && <span>BGM {Math.round(bgmVolume * 100)}%</span>}
              {audioUrl && (
                <span className="clip-canvas-final-preview-modal__mix-audio">
                  <SpeakerWaveIcon className="h-4 w-4" />
                  已接入音频
                </span>
              )}
            </div>
            <div className="clip-canvas-final-preview-modal__transport-controls">
              <button
                type="button"
                className="clip-canvas-final-preview-modal__transport-button"
                onClick={() => seekToLocalTime(currentTime - 1)}
                aria-label="后退 1 秒"
                title="后退 1 秒"
              >
                <BackwardIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                className="clip-canvas-final-preview-modal__transport-button is-play"
                onClick={togglePlayback}
                aria-label={isPlaying ? '暂停预览' : '播放预览'}
                title={isPlaying ? '暂停预览' : '播放预览'}
              >
                {isPlaying ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
              </button>
              <button
                type="button"
                className="clip-canvas-final-preview-modal__transport-button"
                onClick={() => seekToLocalTime(currentTime + 1)}
                aria-label="前进 1 秒"
                title="前进 1 秒"
              >
                <ForwardIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="clip-canvas-final-preview-modal__time">
              {formatTime(currentTime)} / {formatTime(clipDuration)}
            </div>
          </div>
        </div>
      </div>
      <GlobalLoading isOpen={isSavingTrim} message="正在保存裁切..." />
    </div>
  );
}

export default function ClipFinalPreview({ clip }: ClipFinalPreviewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const media = useMemo(() => getFinalPreviewMedia(clip), [
    clip.endImage,
    clip.startImage,
    clip.storyboardImage,
    clip.videoUrl,
  ]);
  const audioUrl = clip.scriptAudioUrl || clip.soundEffectAudioUrl;
  const canPreview = !!media || !!audioUrl;

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [close, isOpen]);

  return (
    <>
      <div className="clip-canvas-final-preview">
        <div className="clip-canvas-final-preview__header">
          <span>最终预览</span>
          <span>{media?.label ?? (audioUrl ? '配音' : '暂无内容')}</span>
        </div>

        <button
          type="button"
          className="clip-canvas-final-preview__stage nodrag nopan nowheel"
          onClick={(event) => {
            event.stopPropagation();
            if (canPreview) {
              setIsOpen(true);
            }
          }}
          disabled={!canPreview}
          aria-label="播放最终预览"
          title="播放最终预览"
        >
          {media?.type === 'video' ? (
            <video src={media.url} preload="metadata" muted playsInline />
          ) : media?.type === 'image' ? (
            <img src={media.url} alt={media.label} />
          ) : (
            <div className="clip-canvas-final-preview__empty">
              <VideoCameraIcon className="h-8 w-8" />
              <span>暂无最终预览</span>
            </div>
          )}

          {canPreview && (
            <span className="clip-canvas-final-preview__play">
              <PlayIcon className="h-5 w-5" />
            </span>
          )}
        </button>

        {audioUrl && (
          <div className="clip-canvas-final-preview__audio">
            <SpeakerWaveIcon className="h-4 w-4" />
            <span>{clip.scriptAudioUrl ? '包含配音' : '包含音效'}</span>
          </div>
        )}
      </div>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <ClipFinalPreviewModal clip={clip} onClose={close} />,
        document.body,
      )}
    </>
  );
}
